import type { CohortCourse } from '$lib/types';

// Free live sessions (served under /courses). Distinct from the paid courses
// in courses.ts. Content follows the Agentic AI Masterclass deck
// (~/dev/akonia_masterclasses/decks/Agentic_AI_Masterclass.pptx) — talk,
// live one-liner→MVP build, Q&A — with the deck's own step timings.
export const cohortCourses: CohortCourse[] = [
	{
		// Distinct slug from the paid course: the free session has its own
		// detail page (static route /courses/free-agentic-ai-masterclass —
		// agenda + enroll dialog), while the paid 10-week course owns
		// /courses/agentic-ai-masterclass. The "Free" title prefix keeps the
		// two catalog cards distinct.
		slug: 'free-agentic-ai-masterclass',
		title: 'Free Agentic AI Masterclass',
		description:
			'From one-liner to shipped product with spec-driven development. One free live session: why agentic AI changes everything, then a real build — product brief, PRD, architecture, stories — executed by AI agents while you watch, ending with a working, deployed app.',
		level: 'intermediate',
		tags: ['Free', 'Live Session', '90 Minutes', 'Live Build Demo'],
		price: 0,
		learn: [
			'Why vibe coding fails and spec-driven development wins',
			'How AI agents absorb the traditional 8-role team — the BMAD agent lineup',
			'Specs as contracts: product brief → PRD → architecture → stories',
			'What an agent build looks like — /bmad-dev-story reading a story and shipping it',
			'Validating against acceptance criteria before you ship',
			'One-liner → shipped product is real — and repeatable'
		],
		agenda: [
			{
				title: 'Why agentic AI',
				items: [
					{ title: 'Vibe coding vs traditional development', duration: '5 min' },
					{ title: 'SDLC evolution — waterfall → agile → AI + spec-driven', duration: '10 min' },
					{ title: 'Meet your AI team — the BMAD agents', duration: '10 min' }
				]
			},
			{
				title: 'Live build — one-liner to shipped MVP',
				items: [
					{ title: 'Step 1 · Product brief — /bmad-create-product-brief', duration: '2 min' },
					{ title: 'Step 2 · PRD — /bmad-create-prd', duration: '3 min' },
					{ title: 'Step 3 · Architecture — /bmad-create-architecture', duration: '3 min' },
					{ title: 'Step 4 · Epics & stories — /bmad-create-epics-and-stories', duration: '2 min' },
					{ title: 'Step 5 · Agent build — /bmad-dev-story', duration: '8 min' },
					{ title: 'Step 6 · Ship — tests, build, working app', duration: '5 min' }
				]
			},
			{
				title: 'Wrap-up',
				items: [
					{ title: 'Key takeaways — process over tools', duration: '5 min' },
					{ title: 'Q&A', duration: '10 min' }
				]
			}
		],
		highlight: {
			title: 'One line in, a shipped app out',
			description:
				'The challenge: “a website that tracks volunteering hours…” — watch it become a working MVP with auth, CRUD, responsive UI and passing tests in about 90 minutes, using the same spec-driven pipeline you can run yourself.'
		},
		nextStep: {
			label: 'Join the 10-Week Masterclass',
			href: '/courses/agentic-ai-masterclass',
			description:
				'Keep going with the full spec-driven program — hands-on projects with AI agents, portfolio-ready by the end.'
		},
		cohort: {
			// Recurring free session: every Monday & Wednesday, 7:00 PM IST
			// (2026-10-18 founder correction). startDate anchors "next session"
			// labels — the first Monday on/after it; displayed times localize
			// to the visitor's timezone client-side (LocalSessionTime).
			startDate: '2026-10-19', // Monday
			timeLabel: '7:00 PM IST',
			timezone: 'Asia/Kolkata',
			timezoneOffset: '+05:30',
			sessionDays: ['MO', 'WE'],
			cadence: 'Every Monday & Wednesday · 90 min',
			sessionLength: '90 min',
			weeks: 8 // ICS RRULE span: 16 sessions (~2 months)
		}
	}
];

export function getCohortCourseBySlug(slug: string): CohortCourse | undefined {
	return cohortCourses.find((course) => course.slug === slug);
}

export function getOtherCohortCourses(currentSlug: string, limit: number = 3): CohortCourse[] {
	return cohortCourses
		.filter((course) => course.slug !== currentSlug)
		.slice(0, limit);
}
